import { useThunkDispatch } from "../../store";
import {
    fetchDatasourceByName,
    refreshDatasourceByName,
    patchDatasourceData,
    setDatasourceData,
    updateParameterValue,
    setDefaultParameterValue,
    updateCurrentCanvasState,
    applyParameterValueChanges,
    goToID,
    goToXYZ,
} from "../../store/storyline/actions";
import {
    showError,
    showSuccess,
    showDetailedError,
    showWarning,
    showInfo,
} from "../../store/notifications/actions";
import {
    switchBranch,
    showBanner,
    dismissBanner,
} from "../../store/app/actions";
import { useMemo } from "react";

/**
 * A hook which provides access to all the actions available to the application/plugins.
 * 
 * For convenience, the returned functions are wrapped in a `dispatch` call and can be invoked directly.
 * 
 * @returns An object containing all the dispatched actions for the application.
 */
export const useAppActions = () => {
    const dispatch = useThunkDispatch();
    const dispatchedActions = useMemo(() => ({
        updateParameterValue: (...params: Parameters<typeof updateParameterValue>) => dispatch(updateParameterValue(...params)),
        setDefaultParameterValue: (...params: Parameters<typeof setDefaultParameterValue>) => dispatch(setDefaultParameterValue(...params)),
        applyParameterValueChanges: (...params: Parameters<typeof applyParameterValueChanges>) => dispatch(applyParameterValueChanges(...params)),
        updateCurrentCanvasState: (...params: Parameters<typeof updateCurrentCanvasState>) => dispatch(updateCurrentCanvasState(...params)),

        fetchDatasourceByName: (...params: Parameters<typeof fetchDatasourceByName>) => dispatch(fetchDatasourceByName(...params)),
        refreshDatasourceByName: (...params: Parameters<typeof refreshDatasourceByName>) => dispatch(refreshDatasourceByName(...params)),
        setDatasourceData: (...params: Parameters<typeof setDatasourceData>) => dispatch(setDatasourceData(...params)),
        patchDatasourceData: (...params: Parameters<typeof patchDatasourceData>) => dispatch(patchDatasourceData(...params)),

        showSuccess: (...params: Parameters<typeof showSuccess>) => dispatch(showSuccess(...params)),
        showInfo: (...params: Parameters<typeof showInfo>) => dispatch(showInfo(...params)),
        showWarning: (...params: Parameters<typeof showWarning>) => dispatch(showWarning(...params)),
        showError: (...params: Parameters<typeof showError>) => dispatch(showError(...params)),
        showDetailedError: (...params: Parameters<typeof showDetailedError>) => dispatch(showDetailedError(...params)),

        goToID: (...params: Parameters<typeof goToID>) => dispatch(goToID(...params)),
        goToXYZ: (...params: Parameters<typeof goToXYZ>) => dispatch(goToXYZ(...params)),

        switchBranch: (...params: Parameters<typeof switchBranch>) => dispatch(switchBranch(...params)),
        showBanner: (...params: Parameters<typeof showBanner>) => dispatch(showBanner(...params)),
        dismissBanner: (...params: Parameters<typeof dismissBanner>) => dispatch(dismissBanner(...params)),
    }), [dispatch]);

    return dispatchedActions;
}